import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import toast from 'react-hot-toast';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  MixIcon,
  PlusIcon,
  CopyIcon,
  EyeNoneIcon,
  TrashIcon,
  Cross2Icon,
  QuestionMarkCircledIcon,
  MagicWandIcon
} from '@radix-ui/react-icons';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import { useApiService } from '../hooks/useApiService';
import { ColumnInfo } from '../services/apiService';
import './ColumnHeaderMenu.css';

export interface ColumnMenuOptions {
  sortDirection?: 'asc' | 'desc' | null;
  isWrapped?: boolean;
  isPinned?: boolean;
  isHidden?: boolean;
  promptDetails?: string;
  formatDetails?: string;
  type?: string;
}

export interface ColumnHeaderMenuProps {
  columnId: string | number;
  columnName: string;
  options: ColumnMenuOptions;
  allColumnNames?: string[];
  onColumnNameChange: (columnId: string | number, name: string) => void;
  onPromptDetailsChange: (columnId: string | number, details: string) => void;
  onFormatDetailsChange: (columnId: string | number, details: string) => void;
  onTypeChange: (columnId: string | number, type: string) => void;
  onSortAscending: (columnId: string | number) => void;
  onSortDescending: (columnId: string | number) => void;
  onClearSort: (columnId: string | number) => void;
  onSelectColumn: (columnId: string | number) => void;
  onWrapText: (columnId: string | number) => void;
  onPinColumn: (columnId: string | number) => void;
  onInsertColumnLeft: (columnId: string | number) => void;
  onInsertColumnRight: (columnId: string | number) => void;
  onDuplicateColumn: (columnId: string | number) => void;
  onHideColumn: (columnId: string | number) => void;
  onDeleteColumn: (columnId: string | number) => void;
}

const ColumnHeaderMenu: React.FC<ColumnHeaderMenuProps> = ({
  columnId,
  columnName,
  options,
  allColumnNames,
  onColumnNameChange,
  onPromptDetailsChange,
  onFormatDetailsChange,
  onTypeChange,
  onSortAscending,
  onSortDescending,
  onClearSort,
  onSelectColumn,
  onWrapText,
  onPinColumn,
  onInsertColumnLeft,
  onInsertColumnRight,
  onDuplicateColumn,
  onHideColumn,
  onDeleteColumn,
}) => {
  const [open, setOpen] = React.useState(false);
  const [hasFailedGeneration, setHasFailedGeneration] = React.useState(false);
  const promptTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const formatTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  // API service hook for AI-powered features
  const { 
    generateFormatDetails, 
    isLoading: apiLoading, 
  } = useApiService();

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      // Reset height to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate the desired height based on content
      const scrollHeight = textarea.scrollHeight;
      const minHeight = 60;
      const maxHeight = 200;
      
      // Set height between min and max, enable scrolling if needed
      if (scrollHeight <= maxHeight) {
        textarea.style.height = `${Math.max(minHeight, scrollHeight)}px`;
        textarea.style.overflowY = 'hidden'; // Hide scrollbar when not needed
      } else {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto'; // Show scrollbar when content overflows
      }
    }
  };

  // Auto-generate format details using AI
  const handleGenerateFormatDetails = async (isManual: boolean = false) => {
    if (!columnName || !columnName.trim()) {
      if (isManual) {
        toast.error('Please enter a column name first');
      }
      return;
    }

    try {
      // Column context information for AI
      const columnInfo: ColumnInfo = {
        currentColumnName: columnName,
        allColumnNames: allColumnNames || [],
        // Additional context that might be useful for AI
        columnType: options.type || 'Text',
        existingPromptDetails: options.promptDetails || '',
        existingFormatDetails: options.formatDetails || ''
      };

      const formatDetails = await generateFormatDetails(columnInfo);
      
      if (formatDetails) {
        // Update the format details in the component
        onFormatDetailsChange(columnId, formatDetails);
        setHasFailedGeneration(false); // Reset failure flag on success
        
        if (isManual) {
          toast.success('Format suggestions generated successfully!');
        }
        
        // Auto-resize the textarea after content is updated
        setTimeout(() => {
          autoResizeTextarea(formatTextareaRef.current);
        }, 100);
      } else {
        // Set failure flag if no result was returned
        setHasFailedGeneration(true);
        if (isManual) {
          toast.error('Failed to generate format suggestions. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error generating format details:', error);
      setHasFailedGeneration(true); // Set failure flag on error
      
      if (isManual) {
        toast.error('Unable to generate format suggestions. Please check your connection and try again.');
      }
      // Error will be handled by the useApiService hook automatically
    }
  };

  // Auto-resize textareas when content changes
  React.useEffect(() => {
    autoResizeTextarea(promptTextareaRef.current);
  }, [options.promptDetails]);

  React.useEffect(() => {
    autoResizeTextarea(formatTextareaRef.current);
  }, [options.formatDetails]);

  // Auto-resize textareas when menu opens (to handle loaded content after page reload)
  React.useEffect(() => {
    if (open) {
      // Small delay to ensure the DOM is ready and content is rendered
      const timer = setTimeout(() => {
        autoResizeTextarea(promptTextareaRef.current);
        autoResizeTextarea(formatTextareaRef.current);
      }, 10);
      
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Auto-generate format details when menu closes (if conditions are met)
  React.useEffect(() => {
    // Only trigger when menu closes (open changes from true to false)
    if (!open && columnName && columnName.trim() !== '' && 
        (!options.formatDetails || options.formatDetails.trim() === '') && 
        !apiLoading && !hasFailedGeneration) {
      
      handleGenerateFormatDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, columnName, options.formatDetails, apiLoading, hasFailedGeneration]);

  // Reset failure flag when column name changes to allow retry
  React.useEffect(() => {
    setHasFailedGeneration(false);
  }, [columnName]);

  const handleMenuItemClick = (action: () => void) => {
    action();
    setOpen(false);
  };

  const getSortIcon = () => {
    if (options.sortDirection === 'asc') return <ChevronUpIcon className="sort-indicator" />;
    if (options.sortDirection === 'desc') return <ChevronDownIcon className="sort-indicator" />;
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, handler: (value: string) => void) => {
    e.stopPropagation();
    handler(e.target.value);
    
    // Auto-resize if it's a textarea
    if (e.target.tagName === 'TEXTAREA') {
      autoResizeTextarea(e.target as HTMLTextAreaElement);
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <div className="column-header-container" role="button" tabIndex={0} aria-label="Column options">
          <MenuOutlinedIcon className="column-header-menu-icon" sx={{ fontSize: 14 }} />
          <span className="column-header-text">{columnName || ''}</span>
          {getSortIcon()}
          <ChevronDownIcon className="column-header-menu-arrow" />
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="column-header-menu-content"
          sideOffset={0}
          align="center"
          alignOffset={0}
        >
          {/* Column Name Input */}
          <div className="menu-section">
            <div className="input-group">
              <input
                type="text"
                className="column-name-input"
                value={columnName || ''}
                onChange={(e) => handleInputChange(e, (value) => onColumnNameChange(columnId, value))}
                onClick={handleInputClick}
                placeholder="Column name"
              />
              <button 
                className="close-button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <Cross2Icon />
              </button>
            </div>
          </div>

          {/* Prompt Details */}
          <div className="menu-section">
            <div className="textarea-group">
              <label className="textarea-label">
                Prompt Details
                <QuestionMarkCircledIcon className="help-icon" />
              </label>
              <textarea
                className="details-textarea"
                value={options.promptDetails || ''}
                onChange={(e) => handleInputChange(e, (value) => onPromptDetailsChange(columnId, value))}
                onClick={handleInputClick}
                placeholder="Enter the name of the retailer selling this product"
                rows={3}
                ref={promptTextareaRef}
              />
            </div>
          </div>

          {/* Format Details */}
          <div className="menu-section">
            <div className="textarea-group">
              <div className="textarea-header">
                <label className="textarea-label">
                  Format Options
                  <QuestionMarkCircledIcon className="help-icon" />
                </label>
                <button
                  className="generate-button"
                  onClick={() => {
                    setHasFailedGeneration(false); // Reset failure flag for manual retry
                    handleGenerateFormatDetails(true);
                  }}
                  disabled={apiLoading || !columnName}
                  title={`Generate AI-powered format suggestions for "${columnName || 'this column'}". Sends column name, context from ${allColumnNames?.length || 0} other columns, column type, and existing settings to your Firebase formatDetails function.`}
                >
                  <MagicWandIcon className="generate-icon" />
                  {apiLoading ? 'Generating...' : 'Generate'}
                </button>
              </div>
              <textarea
                className="details-textarea"
                value={options.formatDetails || ''}
                onChange={(e) => handleInputChange(e, (value) => onFormatDetailsChange(columnId, value))}
                onClick={handleInputClick}
                placeholder="Optional: Explain how you want the column data to be formatted."
                rows={2}
                ref={formatTextareaRef}
              />
            </div>
          </div>

          {/* Type Dropdown */}
          {/* <div className="menu-section">
            <div className="select-group">
              <label className="select-label">Type</label>
              <select
                className="type-select"
                value={options.type || 'Text'}
                onChange={(e) => handleInputChange(e, (value) => onTypeChange(columnId, value))}
                onClick={handleInputClick}
              >
                <option value="Text">Text</option>
                <option value="Number">Number</option>
                <option value="Date">Date</option>
                <option value="Email">Email</option>
                <option value="URL">URL</option>
              </select>
              <ChevronDownIcon className="select-arrow" />
            </div>
          </div> */}

          <div className="menu-separator" />

          {/* Sort Options */}
          <div className="menu-section">
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onSortAscending(columnId))}
            >
              <ChevronUpIcon />
              <span>Sort Ascending</span>
              <span className="shortcut">A</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onSortDescending(columnId))}
            >
              <ChevronDownIcon />
              <span>Sort Descending</span>
              <span className="shortcut">D</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onClearSort(columnId))}
              disabled={!options.sortDirection}
            >
              <MixIcon />
              <span>Clear Sort</span>
              <span className="shortcut">C</span>
            </button>
          </div>

          <div className="menu-separator" />

          {/* Column Options */}
          {/* <div className="menu-section">
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onSelectColumn(columnId))}
            >
              <ColumnSpacingIcon />
              <span>Select Column</span>
              <span className="shortcut">S</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onWrapText(columnId))}
            >
              <TextIcon />
              <span>Wrap Text</span>
              <span className="shortcut">W</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onPinColumn(columnId))}
            >
              <PinLeftIcon />
              <span>Pin Column</span>
              <span className="shortcut">P</span>
            </button>
          </div> */}

          {/* <div className="menu-separator" /> */}

          {/* Column Management */}
          <div className="menu-section">
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onInsertColumnLeft(columnId))}
            >
              <PlusIcon />
              <span>Insert 1 Column Left</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onInsertColumnRight(columnId))}
            >
              <PlusIcon />
              <span>Insert 1 Column Right</span>
            </button>
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onDuplicateColumn(columnId))}
            >
              <CopyIcon />
              <span>Duplicate Column</span>
              <span className="shortcut">R</span>
            </button>
          </div>

          <div className="menu-separator" />

          {/* Danger Zone */}
          <div className="menu-section">
            <button
              className="menu-item"
              onClick={() => handleMenuItemClick(() => onHideColumn(columnId))}
            >
              <EyeNoneIcon />
              <span>Hide Column</span>
              <span className="shortcut">H</span>
            </button>
            <button
              className="menu-item danger"
              onClick={() => handleMenuItemClick(() => onDeleteColumn(columnId))}
            >
              <TrashIcon />
              <span>Delete Column</span>
              <span className="shortcut">⌘⌫</span>
            </button>
          </div>

        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ColumnHeaderMenu; 